'use client';

import { useCallback, useEffect, useRef, type KeyboardEvent, type RefObject } from 'react';

/** What the keyboard can Tab to. */
const TABBABLE_SELECTOR = [
    'a[href]',
    'button:not(:disabled)',
    'input:not(:disabled):not([type="hidden"])',
    'select:not(:disabled)',
    'textarea:not(:disabled)',
    '[contenteditable="true"]',
    '[tabindex]:not([tabindex="-1"])',
].join(', ');

/** Spread onto the dialog element. */
export interface DialogBinding<T extends HTMLElement> {
    ref: RefObject<T>;
    onKeyDown: (event: KeyboardEvent<T>) => void;
}

/**
 * The keyboard behavior of a modal dialog, shared by every overlay that
 * takes over the screen (Modal, Notification, the client's cutscenes):
 *
 * - it takes focus when it mounts and hands it back to whatever had it when
 *   it unmounts, so keys pressed while it is open go to it, not to the game;
 * - when focus drops to the page (a click on a non-focusable backdrop), it
 *   takes it back;
 * - Tab and Shift+Tab wrap around inside it;
 * - Escape calls `onEscape`, when given, and goes no further.
 *
 * The dialog element needs `tabIndex={-1}` so it can hold focus itself.
 */
export function useDialog<T extends HTMLElement>(onEscape?: () => void): DialogBinding<T> {
    const ref = useRef<T>(null);
    const onEscapeRef = useRef(onEscape);
    onEscapeRef.current = onEscape;

    useEffect(() => {
        const dialog = ref.current;
        const previouslyFocused = document.activeElement;
        dialog?.focus({ preventScroll: true });

        // Focus dropped to the page (to <body>, as a click on the backdrop
        // leaves it) comes back. Focus moving to another element (a dialog
        // opened over this one) is left be, and so is the window losing focus,
        // which keeps the focused element.
        const handleFocusOut = (event: FocusEvent): void => {
            const isDroppedToPage = document.activeElement === null || document.activeElement === document.body;
            if (event.relatedTarget === null && isDroppedToPage && dialog?.isConnected) {
                dialog.focus({ preventScroll: true });
            }
        };
        dialog?.addEventListener('focusout', handleFocusOut);

        return (): void => {
            dialog?.removeEventListener('focusout', handleFocusOut);
            if (previouslyFocused instanceof HTMLElement && previouslyFocused.isConnected) {
                previouslyFocused.focus({ preventScroll: true });
            }
        };
    }, []);

    const onKeyDown = useCallback((event: KeyboardEvent<T>): void => {
        const dialog = ref.current;
        if (event.key === 'Escape' && onEscapeRef.current !== undefined) {
            event.preventDefault();
            event.stopPropagation();
            onEscapeRef.current();
            return;
        }
        if (event.key !== 'Tab' || dialog === null) {
            return;
        }
        const tabbables = Array.from(dialog.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR));
        const first = tabbables[0];
        const last = tabbables[tabbables.length - 1];
        const active = document.activeElement;
        if (first === undefined || last === undefined) {
            event.preventDefault();
            return;
        }
        if (event.shiftKey && (active === first || active === dialog)) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && active === last) {
            event.preventDefault();
            first.focus();
        }
    }, []);

    return { ref, onKeyDown };
}
